/**
 * Helpers for data-testid attributes used by the acceptance test suite.
 * See Presentation/data-testid-conventions.md for the naming rules.
 */

/**
 * Turns a field name or value into a kebab-case test id segment,
 * e.g. 'companyName' -> 'company-name', 'SE' -> 'se'
 *
 * @param {string|number} value
 * @returns {string}
 */
export function toTestIdPart(value) {
  return String(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()
}

/**
 * Joins a base test id with suffix segments. Returns null when there is no
 * base, so `:data-testid="testIdFor(...)"` renders no attribute at all.
 *
 * @param {string|null} base
 * @param  {...(string|number)} parts
 * @returns {string|null}
 */
export function testIdFor(base, ...parts) {
  if (!base) {
    return null
  }
  return [base, ...parts.map(toTestIdPart)].join('-')
}

/**
 * Tags the parts Vuetify renders inside a select, which can't get attributes
 * from the template: the click target gets '<wrapper id>-trigger' and the
 * selected value '<wrapper id>-value'. Does nothing when the wrapper has no id.
 *
 * @param {HTMLElement} el - wrapper element with a data-testid
 */
export function tagSelectParts(el) {
  const base = el.getAttribute('data-testid')
  if (!base) {
    return
  }
  const trigger = el.querySelector('.v-input__slot')
  if (trigger) {
    trigger.setAttribute('data-testid', `${base}-trigger`)
  }
  const value = el.querySelector('.v-select__selections')
  if (value) {
    value.setAttribute('data-testid', `${base}-value`)
  }
}

export const selectTestParts = {
  inserted: tagSelectParts,
  componentUpdated: tagSelectParts,
}
